import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { TaxonomyPracticeAreaAndFocusAreaMixedOption } from '@axiom/ui';
import {
  Button,
  CondensedHeader,
  DateInput,
  Dropdown,
  FluidButtonLayout,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  FormGroup,
  Input,
  Layout,
  useApi,
  Tooltip,
  DateUtil,
  TaxonomyUtil,
  FormLabel,
  Checkbox,
  DropdownTree,
  Well,
} from '@axiom/ui';
import type { CountryCodesAbbreviationsType } from '@axiom/const';
import {
  CandidatesConst,
  CandidateSearchFilters,
  LanguageConst,
  TaxonomyConst,
} from '@axiom/const';
import type {
  EnvoyCandidateSearchFiltersForm as CandidateFiltersFormType,
  Taxonomy,
  EnvoyCandidateSearchFiltersForm,
  EnvoyCandidateSearchFilters,
} from '@axiom/validation';
import {
  EnvoyCandidateSearchFiltersFormSchema,
  TALENT_DEFAULT_MINIMUM_YEARS_OF_EXPERIENCE,
} from '@axiom/validation';

import { EnvUtil } from '../../utils/env-util';
import { TagApi } from '../../api/generated/tag-api';
import { BarredLocationApi } from '../../api/barred-location-api';
import { LanguagesApi } from '../../api/languages-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { WindowUtil } from '../../utils/window-util';
import { LegacyUserApi } from '../../api/legacy-user-api';
import { UserApi } from '../../api/user-api';
import { isSalesUser } from '../../utils/roles-util';
import { SaveFiltersModal } from '../SaveFiltersModal/SaveFiltersModal';

import {
  employeeTypeOptions,
  talentProfileStatusSelectItems,
  workPreferencesOptions,
} from './candidate-filters-form.const';
import { CandidateFiltersFormUtil } from './candidate-filters-form-util';

const noop = () => {};

export type CandidateFiltersFormProps = {
  filters: EnvoyCandidateSearchFilters;
  taxonomy: Taxonomy;
  handleFilterChange?: (filters: CandidateFiltersFormType) => void;
  isEditable?: boolean;
};

export const CandidateFiltersForm = ({
  handleFilterChange = noop,
  filters,
  taxonomy,
  isEditable,
}: CandidateFiltersFormProps) => {
  const [searchParams] = useSearchParams();

  const removeSavedSearchIdFromParams = () => {
    if (searchParams.has('savedSearchId')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('savedSearchId');
      const search = newParams.toString();
      /** Using window.history.replaceState instead of react-router's
       * setSearchParams because setSearchParams wraps navigation in
       * startTransition, placing it in a separate render lane from the
       * setFilters call in handleFilterChange. This causes two renders
       * of CandidatesList, firing the search API twice.
       * */
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${search ? `?${search}` : ''}`
      );
    }
  };

  /** The default is seeded here rather than in the form's initial values: the
   * Form drops any field matching its initial value, so a min years equal to
   * the default would never reach onSubmit and the search would not re-run. */
  const getMinYearsOnSelection = ({
    hasSelection,
    currentMinYears,
  }: {
    hasSelection: boolean;
    currentMinYears?: number | null;
  }) =>
    hasSelection
      ? (currentMinYears ?? TALENT_DEFAULT_MINIMUM_YEARS_OF_EXPERIENCE)
      : undefined;

  // We just wanna store the initial state so that followup changes don't
  // affect the form
  const [talentFilters, setTalentFilters] = useState<CandidateFiltersFormType>({
    ...filters,
    rollOffDateDropdown:
      CandidateFiltersFormUtil.getInitialRollOffDateDropDownValue(
        filters.daysToSoonestEngagementEnd
      ),
    rollOffDatePicker: DateUtil.addDaysToNow(
      CandidateFiltersFormUtil.getInitialRollOffDatePickerValue(
        filters.daysToSoonestEngagementEnd
      )
    ),
    practiceAreasAndFocusAreasDropdown: [
      ...TaxonomyUtil.convertPracticeAreaEnumsToOptionLabels(
        taxonomy.practiceAreas,
        filters.practiceAreasAndFocusAreas?.practiceAreas || []
      ),
      ...TaxonomyUtil.convertFocusAreaEnumsToOptionLabels(
        taxonomy.practiceAreas,
        taxonomy.focusAreas,
        filters.practiceAreasAndFocusAreas?.focusAreas || []
      ),
    ],
  });
  const [
    { data: languages },
    { data: tags },
    { data: barredLocations },
    { data: ownerUsers },
    { data: currentUser },
  ] = useApi(
    LanguagesApi.readLanguages(),
    TagApi.readTags(),
    BarredLocationApi.readBarredLocations(),
    LegacyUserApi.readUsers(),
    UserApi.readSessionUser()
  );
  const industryOptions = TaxonomyUtil.getIndustryOptions(taxonomy.industries);
  const legalSkillsOptions =
    TaxonomyUtil.getLegalSkillsByFocusAreaOptions(taxonomy);
  const legalTechnicalSkillsOptions =
    TaxonomyUtil.getLegalTechSkillByCategoryOptions(
      taxonomy.legalTechSkills,
      taxonomy.legalTechSkillCategories
    );
  const generalSkillsOptions = TaxonomyUtil.getGeneralSkillOptions(
    taxonomy.generalSkills
  );

  const practiceAreaAndFocusAreaOptions = useMemo(
    () =>
      TaxonomyUtil.getPracticeAreaAndFocusAreaOptions(
        taxonomy.practiceAreas,
        taxonomy.focusAreas
      ),
    [taxonomy]
  );

  const barredLocationOptions = useMemo(
    () => CandidateFiltersFormUtil.getBarredLocationOptions(barredLocations),
    [barredLocations]
  );

  const tagOptions = useMemo(() => tags.filter(t => t.type === 'tag'), [tags]);

  const [showSaveCandidateFiltersModal, setShowSaveCandidateFiltersModal] =
    useState(false);

  const [languageSelected, setLanguageSelected] = useState(
    () => filters.languages ?? []
  );
  const [legalTechSkillSelected, setLegalTechSkillSelected] = useState(
    () => filters.legalTechnicalSkills ?? []
  );
  const businessTitleOptions =
    TaxonomyUtil.formatBusinessTitlesForDropdown(taxonomy);

  return (
    <div>
      <Form
        name="TALENT_FILTERS_FORM"
        initialValues={talentFilters}
        schema={EnvoyCandidateSearchFiltersFormSchema}
        submitOnChange
        onSubmit={(changedData, _actions, formValues) => {
          removeSavedSearchIdFromParams();
          /**
           * With submitOnChange the Form only hands back the fields that differ
           * from its initialValues, so an untouched expandFa is dropped even
           * when it isn't the default. Take it from the validated form values,
           * which the schema guarantees holds at least one option, so the
           * submitted filter always matches what the checkboxes show.
           * */
          const data = {
            ...changedData,
            expandFa: formValues.expandFa,
          };

          const changedDataKeys = Object.keys(data) as Array<
            keyof EnvoyCandidateSearchFiltersForm
          >;

          const sanitizeChangedData = changedDataKeys.reduce<
            Partial<EnvoyCandidateSearchFiltersForm>
          >((acc, propName) => {
            const propValue = data[propName];
            if (
              [
                'rollOffDatePicker',
                'rollOffDateDropdown',
                'practiceAreasAndFocusAreasDropdown',
              ].includes(propName)
            ) {
              return acc;
            } else if (
              [
                'isPriorityTalent',
                'isProfileShared',
                'excludeCrossFulfilled',
              ].includes(propName) &&
              !propValue
            ) {
              (acc as Record<string, unknown>)[propName] = undefined;
            } else if (
              propName === 'compensation' ||
              propName === 'weeklyAvailability' ||
              propName === 'yearsOfExperience'
            ) {
              const typedPropValue =
                propValue as EnvoyCandidateSearchFiltersForm['weeklyAvailability'];

              acc[propName] = {
                start: typedPropValue?.start ?? undefined,
                end: typedPropValue?.end ?? undefined,
              };
            } else if (propName === 'languages' && !propValue) {
              acc[propName] = undefined;
              acc.languageProficiencies = undefined;
            } else if (propName === 'legalTechnicalSkills' && !propValue) {
              acc[propName] = undefined;
              acc.legalTechnicalSkillProficiencies = undefined;
            } else {
              (acc as Record<string, unknown>)[propName] =
                propValue ?? undefined;
            }

            return acc;
          }, {});

          const resultingChanges = {
            ...filters,
            ...sanitizeChangedData,
          } as CandidateFiltersFormType;

          // These are needed by the form but shouldn't
          // be included in the filters
          const {
            rollOffDatePicker: _rollOffDatePicker,
            rollOffDateDropdown: _rollOffDateDropdown,
            ...filteredChanges
          } = resultingChanges;

          handleFilterChange(filteredChanges);
        }}
      >
        {({ values, setValues }) => {
          const addressStateConfig =
            CandidateFiltersFormUtil.getStateDropdownConfig(
              values.addressCountryCode
            );

          return (
            <>
              <Gutter top="8px" bottom="8px">
                <Input
                  placeholder="Search"
                  name="search"
                  disabled={!isEditable}
                  icon="search"
                />
              </Gutter>

              <Well background="light blue">
                <CondensedHeader name="talentExperienceHeader">
                  Talent experience
                </CondensedHeader>
                <Gutter bottom="16px" />
                <Grid>
                  <GridRow>
                    <GridColumn width={9}>
                      <Dropdown
                        label="Practice area/focus"
                        name="practiceAreasAndFocusAreasDropdown"
                        placeholder="Show All"
                        options={practiceAreaAndFocusAreaOptions}
                        onChange={async (value, _event, option) => {
                          const selectedOptions =
                            option as TaxonomyPracticeAreaAndFocusAreaMixedOption[];
                          const practiceAreasAndFocusAreas = value
                            ? {
                                practiceAreas: selectedOptions
                                  .filter(o => o.type === 'PracticeArea')
                                  .map(o => o.value),
                                focusAreas: selectedOptions
                                  .filter(o => o.type === 'FocusArea')
                                  .map(o => o.value),
                              }
                            : { practiceAreas: [], focusAreas: [] };

                          await setValues({
                            ...values,
                            practiceAreasAndFocusAreas,
                            practiceAreasMinYears: getMinYearsOnSelection({
                              hasSelection: CandidateUtil.hasMinYearsSelection({
                                filters: { practiceAreasAndFocusAreas },
                                name: 'practiceAreasMinYears',
                              }),
                              currentMinYears: values.practiceAreasMinYears,
                            }),
                          });
                        }}
                        displayKey="label"
                        valueKey="label"
                        disabled={!isEditable}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        label="Min years"
                        name="practiceAreasMinYears"
                        disabled={
                          !isEditable ||
                          !CandidateUtil.hasMinYearsSelection({
                            filters: values,
                            name: 'practiceAreasMinYears',
                          })
                        }
                        placeholder="2"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
                <FormGroup name="expandFa">
                  <Gutter bottom="4px" />
                  <Checkbox
                    name="expandFa"
                    option={CandidateSearchFilters.expandFa.PRIMARY}
                    displayValue="Include Axiom-set primary focus areas"
                  />
                  <Gutter bottom="4px" />
                  <Checkbox
                    name="expandFa"
                    option={CandidateSearchFilters.expandFa.TOP}
                    displayValue="Include top 5 talent-selected focus areas"
                  />
                  <Gutter bottom="4px" />
                  <Checkbox
                    name="expandFa"
                    option={CandidateSearchFilters.expandFa.ALL}
                    displayValue="Include all talent-selected focus areas"
                  />
                </FormGroup>
                <Gutter bottom="8px" />
                <Dropdown
                  label="Legal role"
                  name="businessTitles"
                  placeholder="Show All"
                  options={businessTitleOptions}
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter top="8px" bottom="8px">
                  <FormLabel>Years of experience</FormLabel>
                </Gutter>
                <Grid columns={2}>
                  <GridRow>
                    <GridColumn>
                      <Input
                        label="From"
                        name="yearsOfExperience.start"
                        disabled={!isEditable}
                        placeholder="0"
                      />
                    </GridColumn>
                    <GridColumn>
                      <Input
                        label="To"
                        name="yearsOfExperience.end"
                        disabled={!isEditable}
                        placeholder="50"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
                <Gutter bottom="8px" />
                <Grid>
                  <GridRow>
                    <GridColumn width={9}>
                      <Dropdown
                        label="Industry"
                        name="industries"
                        options={industryOptions}
                        placeholder="Show All"
                        displayKey="label"
                        valueKey="value"
                        disabled={!isEditable}
                        onChange={async value => {
                          await setValues({
                            ...values,
                            industriesMinYears: getMinYearsOnSelection({
                              hasSelection:
                                Array.isArray(value) && value.length > 0,
                              currentMinYears: values.industriesMinYears,
                            }),
                          });
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        label="Min years"
                        name="industriesMinYears"
                        disabled={
                          !isEditable ||
                          !CandidateUtil.hasMinYearsSelection({
                            filters: values,
                            name: 'industriesMinYears',
                          })
                        }
                        placeholder="2"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
                <Gutter bottom="8px" />
                <Grid>
                  <GridRow>
                    <GridColumn width={9}>
                      <DropdownTree
                        label="Legal skills"
                        disabled={!isEditable}
                        displayKey="label"
                        name="legalSkills"
                        nestedKey="choices"
                        options={legalSkillsOptions}
                        placeholder="Show All"
                        valueKey="value"
                        onChange={async value => {
                          await setValues({
                            ...values,
                            legalSkillsMinYears: getMinYearsOnSelection({
                              hasSelection:
                                Array.isArray(value) && value.length > 0,
                              currentMinYears: values.legalSkillsMinYears,
                            }),
                          });
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        label="Min years"
                        name="legalSkillsMinYears"
                        disabled={
                          !isEditable ||
                          !CandidateUtil.hasMinYearsSelection({
                            filters: values,
                            name: 'legalSkillsMinYears',
                          })
                        }
                        placeholder="2"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
                <Gutter bottom="8px" />
                <Dropdown
                  label="General skills"
                  name="generalSkills"
                  options={generalSkillsOptions}
                  placeholder="Show All"
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter bottom="8px" />
                <Grid>
                  <GridRow>
                    <GridColumn width={7}>
                      <DropdownTree
                        label="Legal tech skills"
                        name="legalTechnicalSkills"
                        placeholder="Show All"
                        options={legalTechnicalSkillsOptions}
                        displayKey="label"
                        valueKey="value"
                        disabled={!isEditable}
                        nestedKey="choices"
                        onChange={async selectedValue => {
                          const legalTechnicalSkillValues = Array.isArray(
                            selectedValue
                          )
                            ? selectedValue.map(String)
                            : [];

                          setLegalTechSkillSelected(legalTechnicalSkillValues);

                          if (legalTechnicalSkillValues.length === 0) {
                            const {
                              legalTechnicalSkillProficiencies: _,
                              ...restValues
                            } = values;
                            await setValues(restValues);
                          }
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={5}>
                      <FormGroup
                        label="Proficiency"
                        name="legalTechnicalSkillProficiencies"
                      >
                        {TaxonomyConst.LimitedLegalTechSkillsProficienciesOptions.map(
                          option => (
                            <Gutter bottom="8px" key={option.value}>
                              <Checkbox
                                name="legalTechnicalSkillProficiencies"
                                displayValue={option.label}
                                option={option.value}
                                disabled={
                                  !isEditable ||
                                  legalTechSkillSelected.length === 0
                                }
                              />
                            </Gutter>
                          )
                        )}
                      </FormGroup>
                    </GridColumn>
                  </GridRow>
                </Grid>
                <Gutter bottom="8px" />
                <Grid>
                  <GridRow>
                    <GridColumn width={7}>
                      <Dropdown
                        label="Languages"
                        name="languages"
                        placeholder="Show All"
                        options={languages.map(language => ({
                          label: language.name,
                          value: language.id,
                        }))}
                        displayKey="label"
                        valueKey="value"
                        disabled={!isEditable}
                        onChange={async selectedValue => {
                          const languageValues = Array.isArray(selectedValue)
                            ? selectedValue.map(String)
                            : [];

                          setLanguageSelected(languageValues);

                          if (languageValues.length === 0) {
                            const { languageProficiencies: _, ...restValues } =
                              values;
                            await setValues(restValues);
                          }
                        }}
                      />
                    </GridColumn>
                    <GridColumn width={5}>
                      <FormGroup
                        label="Proficiency"
                        name="languageProficiencies"
                      >
                        {LanguageConst.LanguageProficienciesOptions.map(
                          (option, index) => (
                            <Gutter
                              bottom={
                                index <
                                LanguageConst.LanguageProficienciesOptions
                                  .length -
                                  1
                                  ? '8px'
                                  : '0px'
                              }
                              key={option.value}
                            >
                              <Checkbox
                                name="languageProficiencies"
                                displayValue={option.label}
                                option={option.value}
                                disabled={
                                  !isEditable || languageSelected.length === 0
                                }
                              />
                            </Gutter>
                          )
                        )}
                      </FormGroup>
                    </GridColumn>
                  </GridRow>
                </Grid>
                <Gutter bottom="8px" />
                <Input
                  label="Companies"
                  name="experienceAtClient"
                  disabled={!isEditable}
                  placeholder=""
                />
                <Gutter bottom="8px" />
                <Layout position="right">
                  <Button
                    name="searchByCompanyExperience"
                    icon="tableau"
                    pattern="secondary"
                    variation="minimal"
                    toTab={EnvUtil.tableauCompanyUrl}
                    title="Open Tableau to find talent with experience at specific companies"
                  >
                    Search by company experience
                  </Button>
                </Layout>
                <Gutter bottom="8px" />
                <Input
                  label="Schools"
                  name="educationInstitution"
                  disabled={!isEditable}
                  placeholder=""
                />
                <Gutter bottom="8px" />
                <Dropdown
                  label="Area of study"
                  name="areaOfStudyCategories"
                  options={CandidateFiltersFormUtil.getAreaOfStudyCategoryOptions()}
                  placeholder="Show All"
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter bottom="8px" />
                <Dropdown
                  label="Tags"
                  name="tags"
                  options={tagOptions}
                  placeholder="Show All"
                  displayKey="name"
                  valueKey="id"
                  disabled={!isEditable}
                />
              </Well>
              <Gutter bottom="8px" />

              <Well background="light blue">
                <CondensedHeader name="fulfilmentHeader">
                  Fulfilment
                </CondensedHeader>
                <Gutter bottom="16px" />
                <Dropdown
                  label="Profile status"
                  name="profileStatus"
                  options={talentProfileStatusSelectItems}
                  placeholder="Show All"
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter top="8px" bottom="8px">
                  <FormLabel>Current availability (hours)</FormLabel>
                </Gutter>
                <Grid columns={2}>
                  <GridRow>
                    <GridColumn>
                      <Input
                        label="From"
                        name="weeklyAvailability.start"
                        disabled={!isEditable}
                        placeholder="0"
                      />
                    </GridColumn>
                    <GridColumn>
                      <Input
                        label="To"
                        name="weeklyAvailability.end"
                        disabled={!isEditable}
                        placeholder="80"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
                <Gutter bottom="8px" />
                <FormGroup>
                  <Grid columns={2}>
                    <GridRow>
                      <GridColumn>
                        <Dropdown
                          label="Roll off date"
                          name="rollOffDateDropdown"
                          options={[
                            { label: 'Any Date', value: null },
                            { label: 'Next 15 Days', value: 15 },
                            { label: 'Next 30 Days', value: 30 },
                            { label: 'Next 45 Days', value: 45 },
                            { label: 'Custom', value: 0 },
                          ]}
                          placeholder="Any Date"
                          displayKey="label"
                          valueKey="value"
                          disabled={!isEditable}
                          onChange={async value => {
                            const numericValue = value as number | null;
                            switch (true) {
                              case numericValue === 0: {
                                await setValues({
                                  ...values,
                                  daysToSoonestEngagementEnd: numericValue,
                                  rollOffDateDropdown: numericValue,
                                  rollOffDatePicker: DateUtil.addDaysToNow(0),
                                });
                                break;
                              }

                              case numericValue === null: {
                                await setValues({
                                  ...values,
                                  daysToSoonestEngagementEnd: numericValue,
                                  rollOffDateDropdown: numericValue,
                                  rollOffDatePicker: null,
                                });
                                break;
                              }

                              default: {
                                await setValues({
                                  ...values,
                                  daysToSoonestEngagementEnd: numericValue,
                                  rollOffDateDropdown: numericValue,
                                  rollOffDatePicker: null,
                                });
                              }
                            }
                          }}
                        />
                      </GridColumn>
                      {values.rollOffDateDropdown === 0 && (
                        <GridColumn>
                          <DateInput
                            name="rollOffDatePicker"
                            label="Rolling Off On/Before"
                            disabled={!isEditable}
                            onChange={async value => {
                              await setValues({
                                ...values,
                                daysToSoonestEngagementEnd: value
                                  ? DateUtil.getDaysFromNow(value as string)
                                  : undefined,
                                rollOffDatePicker: value as string,
                              });
                            }}
                          />
                        </GridColumn>
                      )}
                    </GridRow>
                  </Grid>
                </FormGroup>
                <Gutter top="8px">
                  <Grid columns={2}>
                    <GridRow>
                      <GridColumn>
                        <FormLabel>Total annual compensation</FormLabel>
                      </GridColumn>
                      <GridColumn textAlign="right">
                        <Tooltip>
                          Enter salary in the local currency format
                        </Tooltip>
                      </GridColumn>
                    </GridRow>
                  </Grid>
                </Gutter>
                <Grid columns={2}>
                  <GridRow>
                    <GridColumn>
                      <Input
                        label="From"
                        name="compensation.start"
                        disabled={!isEditable}
                        placeholder="0"
                      />
                    </GridColumn>
                    <GridColumn>
                      <Input
                        label="To"
                        name="compensation.end"
                        disabled={!isEditable}
                        placeholder="2250000"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
                <Gutter bottom="8px" />
                <Dropdown
                  label="Bar admission"
                  name="barredLocations"
                  options={barredLocationOptions}
                  placeholder="Show All"
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter bottom="8px" />
                <Well background="light">
                  <Dropdown
                    label="Address country"
                    name="addressCountryCode"
                    options={CandidateFiltersFormUtil.getAddressCountries()}
                    placeholder="Show All"
                    displayKey="label"
                    valueKey="value"
                    disabled={!isEditable}
                    onChange={async value => {
                      await setValues({
                        ...values,
                        addressCountryCode:
                          value as CountryCodesAbbreviationsType,
                        addressStates: null,
                      });
                    }}
                  />
                  <Gutter bottom="8px" />
                  {addressStateConfig ? (
                    <>
                      <Dropdown
                        label={addressStateConfig.label}
                        name="addressStates"
                        options={addressStateConfig.options}
                        placeholder="Show All"
                        displayKey="label"
                        valueKey="value"
                        disabled={!isEditable}
                      />
                      <Gutter bottom="8px" />
                    </>
                  ) : null}
                  <Layout position="right">
                    <Button
                      icon="tableau"
                      pattern="secondary"
                      variation="minimal"
                      toTab={EnvUtil.tableauLocationUrl}
                      title="Open Tableau to find talent in specific geographic areas"
                    >
                      View US talent map
                    </Button>
                  </Layout>
                </Well>
                <Gutter bottom="8px" />
                <Dropdown
                  label="Employee type"
                  name="employeeType"
                  placeholder="Show All"
                  options={employeeTypeOptions}
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter bottom="8px" />
                <Dropdown
                  label="Work preferences"
                  name="workPreferences"
                  options={workPreferencesOptions}
                  placeholder="Show All"
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter bottom="8px" />
                <Dropdown
                  label="Talent owner"
                  name="ownerUserId"
                  options={ownerUsers.map(user => ({
                    label: user.fullName,
                    value: user.id,
                  }))}
                  placeholder="Show All"
                  displayKey="label"
                  valueKey="value"
                  disabled={!isEditable}
                />
                <Gutter bottom="8px" />
                <Checkbox
                  name="isProfileShared"
                  disabled={!isEditable}
                  displayValue="Include only talent open to proactive submissions"
                />
                <Gutter bottom="8px" />
                <Checkbox
                  name="isPriorityTalent"
                  disabled={!isEditable}
                  displayValue="Include only priority talent"
                />
                <Gutter bottom="8px" />
                <Checkbox
                  name="excludeCrossFulfilled"
                  disabled={!isEditable}
                  displayValue="Exclude cross-fulfilled (selected, or interviewing for 3 or more opps)"
                />
              </Well>
              {isEditable ? (
                <Gutter top="40px">
                  <FluidButtonLayout>
                    <Button
                      name="RESETFILTERSBUTTON"
                      onClick={async () => {
                        removeSavedSearchIdFromParams();
                        // Need to have this take all values in the form currently and set them to undefined,
                        // Then add the defaults back in
                        const urlProps = WindowUtil.getUrlQuery();
                        const resetFormValues = Object.keys(urlProps).reduce<
                          Record<string, unknown>
                        >((acc, propName) => {
                          acc[propName] = null;
                          return acc;
                        }, {});

                        const newResetData = {
                          ...resetFormValues,
                          ...CandidateUtil.getCandidatesListFilterDefaults(),
                        };

                        if (isSalesUser(currentUser)) {
                          newResetData.profileStatus =
                            newResetData.profileStatus.filter(
                              value =>
                                value !==
                                CandidatesConst.ProfileStatuses.InDiligence
                            );
                        }

                        await setValues({
                          ...newResetData,
                          rollOffDateDropdown: null,
                          rollOffDatePicker: null,
                        });
                        setTalentFilters({ ...newResetData });
                        setLanguageSelected([]);
                        setLegalTechSkillSelected([]);

                        handleFilterChange(newResetData);
                      }}
                      variation="outline"
                    >
                      Reset Filters
                    </Button>
                    <Button
                      name="SAVESEARCHBUTTON"
                      onClick={() => {
                        setShowSaveCandidateFiltersModal(true);
                      }}
                    >
                      Save Search
                    </Button>
                  </FluidButtonLayout>
                </Gutter>
              ) : null}
            </>
          );
        }}
      </Form>
      {showSaveCandidateFiltersModal ? (
        <SaveFiltersModal
          filters={filters}
          onClose={() => {
            setShowSaveCandidateFiltersModal(false);
          }}
          type="candidate"
        />
      ) : null}
    </div>
  );
};
